import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import clsx from 'clsx';
import { useUi, useAssetCdn } from '@/context/Ui';
import { Lines, SymbolType, CdnObjectType } from '@/types';
import { AudioPlayer, CanvasWrite } from '@/components';
import { ActivityTitleStep } from '../components/ActivityTitleStep';
import { Toolbar } from '../components/Toolbar';
import { WhitePanel } from '../components/WhitePanel';
import { Underlines } from '../components/Underlines';
import { Activity, ActivityStep } from '../Activity';
import { ActivityTypeProps } from '../types';

interface WriteSentenceInputProps {
  sentence: string;
  showAnswer: boolean;
}

interface WriteSentenceInput {
  asBitmap: () => string | undefined;
  asVector: () => Lines | undefined;
}

const WriteSentenceInput = forwardRef<
  WriteSentenceInput,
  WriteSentenceInputProps
>(({ sentence, showAnswer }, ref) => {
  const writeRef = useRef<CanvasWrite>(null);
  const playerRef = useRef<AudioPlayer>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const { sendAlert } = useUi();

  const mediaUrl = useAssetCdn(
    sentence,
    SymbolType.Sentence,
    CdnObjectType.Audio,
  );

  useImperativeHandle(ref, () => ({
    asBitmap: () => writeRef.current?.asBitmap(),
    asVector: () => writeRef.current?.asVector(),
  }));

  const [canvasWidth, setCanvasWidth] = useState(768);
  const [canvasHeight, setCanvasHeight] = useState(576);

  const [playedOnce, setPlayedOnce] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (panelRef.current === null) {
        return;
      }

      const rect = panelRef.current.getBoundingClientRect();
      const style = window.getComputedStyle(panelRef.current);

      const paddingX =
        parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
      const paddingY =
        parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
      const borderX =
        parseFloat(style.borderLeftWidth) + parseFloat(style.borderRightWidth);
      const borderY =
        parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);

      const availableWidth = rect.width - paddingX - borderX;
      const availableHeight = rect.height - paddingY - borderY;

      setCanvasWidth(availableWidth || 768);
      setCanvasHeight(availableHeight || 576);
    };

    window.addEventListener('resize', handleResize);

    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      <Toolbar
        onPlay={() => {
          playerRef.current?.play();
          setPlayedOnce(true);
        }}
        hintPlay={!playedOnce}
        playDisabled={!playerReady}
        onRefresh={() => writeRef.current?.reset()}
      />

      <WhitePanel
        ref={panelRef}
        className="w-full flex-grow"
        mode="basic"
        color="paper"
      >
        <div className="relative overflow-hidden h-full">
          <CanvasWrite
            ref={writeRef}
            className={clsx('mx-auto bg-blue-50')}
            drawWidth={canvasWidth}
            drawHeight={canvasHeight}
            fill={true}
          />

          {/* Position guides by percentage of canvas height to keep them visible on small screens */}
          <Underlines
            length={1}
            wordSoundsLength={1}
            letterWidthClass="w-full"
            positionTopPercent={25}
          />

          <Underlines
            length={1}
            wordSoundsLength={1}
            letterWidthClass="w-full"
            positionTopPercent={50}
          />

          <Underlines
            length={1}
            wordSoundsLength={1}
            letterWidthClass="w-full"
            positionTopPercent={75}
          />
        </div>
      </WhitePanel>

      <p
        className={clsx(
          'relative my-1 top-1 text-black text-3xl xl:text-5xl font-bold text-center',
          'transition-all duration-300 ease-in-out font-sassoonInfant',
          showAnswer ? 'opacity-100' : 'opacity-0',
        )}
      >
        {sentence}
      </p>

      <AudioPlayer
        ref={playerRef}
        src={mediaUrl || ''}
        onReadySrc={() => setPlayerReady(true)}
        onLoadTimeoutSrc={() =>
          sendAlert('We could not load some of the audio for this lesson.', {
            type: 'warning',
            title: 'There was a problem',
          })
        }
      />
    </>
  );
});

WriteSentenceInput.displayName = 'WriteSentenceInput';

interface WriteSentenceStepProps extends ActivityTypeProps {
  total: number;
  index: number;
  sentence: string;
}

const WriteSentenceStep = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
  total,
  index,
  sentence,
}: WriteSentenceStepProps) => {
  const writeSentenceInputRef = useRef<WriteSentenceInput | null>(null);

  const [showAnswer, setShowAnswer] = useState<boolean>(false);

  return (
    <ActivityStep
      stepIdentifier={`${activity.id}-${index}-1`}
      activity={activity}
      mode={mode}
      stepNumber={index + 1}
      totalSteps={total}
      wrapperClassName={wrapperClassName}
      isPreview={isPreview}
      usePanel
      title={'Click to hear the sentence. Write the sentence.'}
      showTitle={mode === 'TEACH'}
      noBottomPadding
      showActivityPagination={false}
      align="top"
      onBeforeLeave={() => {
        if (!showAnswer) {
          setShowAnswer(true);

          return false;
        }

        return true;
      }}
      getActivityResponseMetaData={() => ({
        text: sentence,
        base64Image: writeSentenceInputRef.current?.asBitmap(),
        points: JSON.stringify(writeSentenceInputRef.current?.asVector()),
      })}
    >
      <WriteSentenceInput
        ref={writeSentenceInputRef}
        sentence={sentence}
        showAnswer={showAnswer}
      />
    </ActivityStep>
  );
};

export const SpellingWriteSentences = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
}: ActivityTypeProps) => {
  if (!activity.sentences) {
    throw new Error('Sentences are required for this activity');
  }

  const { sentences } = activity;

  return (
    <Activity
      steps={[
        ...(mode === 'TEACH'
          ? [
              <ActivityTitleStep
                key={`${activity.id}-title`}
                activity={activity}
                mode={mode}
                wrapperClassName={wrapperClassName}
                isPreview={isPreview}
                index={0}
                total={sentences.length + 1}
              />,
            ]
          : []),
        ...sentences.flatMap((sentence, index) => [
          <WriteSentenceStep
            key={`${activity.id}-${index}`}
            activity={activity}
            mode={mode}
            wrapperClassName={wrapperClassName}
            isPreview={isPreview}
            index={index + 1}
            total={sentences.length + 1}
            sentence={sentence}
          />,
        ]),
      ]}
    />
  );
};
